import React, { useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatCount } from "@/lib/format";
import {
  Sparkles, Wallet, TrendingUp, TrendingDown, Users, Eye, Home, CreditCard,
  AlertTriangle, Building2, Layers, Minus,
} from "lucide-react";

const DASH = "—";

function DeltaChip({ value, invert = false, testid }) {
  if (value === null || value === undefined) return <span className="text-[10px] text-muted-foreground">{DASH}</span>;
  const up = value > 0.0005;
  const down = value < -0.0005;
  const good = invert ? down : up;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const cls = !up && !down ? "text-muted-foreground"
    : good ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${cls}`} data-testid={testid}>
      <Icon className="h-3 w-3" /> {(Math.abs(value) * 100).toFixed(1)}%
    </span>
  );
}

function StatRow({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5" data-testid={testid}>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${accent}1f`, color: accent }}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className="num truncate font-heading text-lg font-semibold tabular-nums">{value}</p>
      </div>
      {sub && <div className="shrink-0 text-right">{sub}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">{children}</p>;
}

function FunnelMini({ label, icon: Icon, actual, target, accent, testid }) {
  const pct = target ? Math.min(100, Math.round((actual / target) * 100)) : null;
  return (
    <div className="rounded-lg border bg-card p-2.5" data-testid={testid}>
      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Icon className="h-3 w-3" style={{ color: accent }} /> {label}</p>
      <p className="num mt-0.5 font-heading text-base font-semibold tabular-nums">{formatCount(actual)}</p>
      <p className="text-[10px] text-muted-foreground">of {target ? formatCount(target) : DASH} target{pct !== null ? ` · ${pct}%` : ""}</p>
      {pct !== null && (
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[hsl(var(--surface-3))]">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
        </div>
      )}
    </div>
  );
}

/** CEO Digest — one-tap computed summary of spend, funnel & payment health. */
export function CeoDigestSheet() {
  const { money } = useCurrency();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const openDigest = () => {
    setOpen(true);
    setData(null);
    api.ceoDigest().then(setData).catch(() => setData({ error: true }));
  };

  const s = data?.spend || {};
  const f = data?.funnel || {};
  const p = data?.payments || {};
  const a = data?.budget_alerts || {};
  const mom = s.mom_change;
  const wow = s.prior_7_days ? (s.last_7_days - s.prior_7_days) / s.prior_7_days : null;
  const paidPct = p.billed ? Math.round((p.paid / p.billed) * 100) : 0;

  return (
    <>
      <Button variant="outline" size="sm" className="h-9 gap-1.5 border-[hsl(var(--accent-gold,45_65%_52%)/0.5)]" onClick={openDigest} data-testid="ceo-digest-button">
        <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> CEO Digest
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md" data-testid="ceo-digest-sheet">
          <SheetHeader className="border-b p-5">
            <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" /> CEO Digest</SheetTitle>
            <SheetDescription data-testid="ceo-digest-asof">
              {data?.as_of ? `Spend, funnel & payment health · data as of ${data.as_of}` : "Computing the latest numbers\u2026"}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-5">
            {!data ? (
              <div className="space-y-3">{Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
            ) : data.error ? (
              <p className="text-sm text-muted-foreground">Could not compute the digest. Please retry.</p>
            ) : (
              <>
                <SectionLabel>Spend pulse</SectionLabel>
                <div className="space-y-2">
                  <StatRow icon={Wallet} label={`${data.current_month || "Current month"} spend`} value={money(s.current_month || 0)}
                    accent="hsl(var(--primary))" testid="digest-spend-month"
                    sub={<div className="text-right"><DeltaChip value={mom} invert testid="digest-spend-mom" /><p className="text-[10px] text-muted-foreground">vs {data.previous_month || "prev"}</p></div>} />
                  <StatRow icon={TrendingUp} label="Last 7 days" value={money(s.last_7_days || 0)}
                    accent="hsl(var(--chart-cat-5))" testid="digest-spend-week"
                    sub={<div className="text-right"><DeltaChip value={wow} invert testid="digest-spend-wow" /><p className="text-[10px] text-muted-foreground">vs prior 7 days</p></div>} />
                  <StatRow icon={Layers} label="FY total spend" value={money(s.fy_total || 0)} accent="hsl(var(--chart-cat-7))" testid="digest-spend-fy" />
                </div>
                {(s.top_projects || []).length > 0 && (
                  <div className="mt-2 rounded-lg border bg-[hsl(var(--surface-2))] p-2.5" data-testid="digest-top-projects">
                    <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"><Building2 className="h-3 w-3" /> Top spenders · {data.current_month}</p>
                    {(s.top_projects || []).map((t) => (
                      <div key={t.name} className="flex items-center justify-between py-0.5 text-xs">
                        <span>{t.name}</span><span className="num font-medium tabular-nums">{money(t.spend)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <SectionLabel>Funnel health · {f.month || DASH}</SectionLabel>
                <div className="grid grid-cols-3 gap-2" data-testid="digest-funnel">
                  <FunnelMini label="Leads" icon={Users} actual={f.leads?.actual || 0} target={f.leads?.target || 0} accent="hsl(var(--chart-cat-4))" testid="digest-funnel-leads" />
                  <FunnelMini label="Site Visits" icon={Eye} actual={f.site_visits?.actual || 0} target={f.site_visits?.target || 0} accent="hsl(var(--chart-cat-3))" testid="digest-funnel-sv" />
                  <FunnelMini label="Bookings" icon={Home} actual={f.bookings?.actual || 0} target={f.bookings?.target || 0} accent="hsl(var(--success))" testid="digest-funnel-bookings" />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]" data-testid="digest-conversions">
                  <Badge variant="outline" className="gap-1 font-normal">Lead→SV <b className="tabular-nums">{f.lead_to_sv != null ? `${(f.lead_to_sv * 100).toFixed(1)}%` : DASH}</b>{f.benchmark_lead_to_sv != null && <span className="text-muted-foreground">/ bench {f.benchmark_lead_to_sv}%</span>}</Badge>
                  <Badge variant="outline" className="gap-1 font-normal">SV→Booking <b className="tabular-nums">{f.sv_to_booking != null ? `${(f.sv_to_booking * 100).toFixed(1)}%` : DASH}</b>{f.benchmark_sv_to_booking != null && <span className="text-muted-foreground">/ bench {f.benchmark_sv_to_booking}%</span>}</Badge>
                </div>

                <SectionLabel>Payment health</SectionLabel>
                <div className="space-y-2">
                  <StatRow icon={CreditCard} label="Paid" value={money(p.paid || 0)} accent="hsl(var(--success))" testid="digest-paid"
                    sub={<Badge variant="outline" className="tabular-nums text-[10px]">{paidPct}% of billed</Badge>} />
                  <StatRow icon={Wallet} label="Outstanding" value={money(p.outstanding || 0)} accent="hsl(var(--warning))" testid="digest-outstanding"
                    sub={<Badge variant="outline" className="tabular-nums text-[10px]">{formatCount(p.unpaid_count || 0)} unpaid lines</Badge>} />
                </div>

                <SectionLabel>Budget alerts</SectionLabel>
                {(a.over_count || 0) + (a.near_count || 0) === 0 ? (
                  <p className="rounded-lg border border-[hsl(var(--success)/0.35)] bg-[hsl(var(--success)/0.07)] px-3 py-2 text-xs text-[hsl(var(--success))]" data-testid="digest-alerts-ok">
                    All projects are within their monthly budget plan.
                  </p>
                ) : (
                  <div className="space-y-1.5" data-testid="digest-alerts">
                    <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--warning))]"><AlertTriangle className="h-3.5 w-3.5" /> {a.over_count} over · {a.near_count} nearing the plan</p>
                    {(a.top || []).map((al) => (
                      <div key={`${al.project}-${al.month}`} className="flex items-center justify-between rounded-md border px-2.5 py-1.5 text-xs">
                        <span>{al.project} · {al.month}</span>
                        <span className="num tabular-nums text-[hsl(var(--danger))]">{Math.round(al.ratio * 100)}% of cap</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
